import angular from 'angular';
import jstimezonedetect from 'jstimezonedetect';

import HttpService from './http.service';
import Subscribable from 'common/provider/subscribable.provider';

const HEADER_AUTHORIZATION  = 'Authorization';
const HEADER_PLATFORM       = 'X-App-Platform';
const HEADER_VERSION        = 'X-App-Version';
const HEADER_TIMEZONE       = 'X-Timezone';

export default class ApiService extends Subscribable(HttpService) {
    /* @ngInject */
    constructor($http, $q, $injector, diagnosticsService) {
        super($http);

        this.$q = $q;
        
        this.api = null;
        this.version = null;
        this.token = null;
        this.$injector = $injector;
        this.diagnosticsService = diagnosticsService;
    }

    onlyAuthorized() {
        return this.token ? this.$q.resolve() : this.$q.reject();
    }

    get(url, config) {
        return this.handleRequest(() => super.get(this.updateUrl(url), this.attachAuthorizationHeader(config)));
    }

    delete(url, config) {
        return this.handleRequest(() => super.delete(this.updateUrl(url), this.attachAuthorizationHeader(config)));
    }

    post(url, data, config) {
        return this.handleRequest(() => super.post(this.updateUrl(url), data, this.attachAuthorizationHeader(config)));
    }

    put(url, data, config) {
        return this.handleRequest(() => super.put(this.updateUrl(url), data, this.attachAuthorizationHeader(config)));
    }

    patch(url, data, config) {
        return this.handleRequest(() => super.patch(this.updateUrl(url), data, this.attachAuthorizationHeader(config)));
    }

    attachAuthorizationHeader(config) {
        config = angular.merge({
            withCredentials: true
        }, config || {});

        if (this.token && !config.anonymous) {
            config = angular.merge({}, config, {
                headers: {
                    [HEADER_AUTHORIZATION]: this.token,
                    [HEADER_PLATFORM]: 'Web',
                    [HEADER_VERSION]: this.version,
                    [HEADER_TIMEZONE]: jstimezonedetect.determine().name()
                }
            });
        }

        return config;
    }

    handleRequest(callback, preventRefresh) {
        return callback().catch((result) => {
            result = result || {};
            
            if (result.status === 401 && !preventRefresh) {
                this.diagnosticsService.warn('api_request_401_refresh_requested', {
                    hasToken: !!this.token
                });
                return this.requestTokenRefresh().then(() => this.handleRequest(callback, true));
            } else if (result.data.error === 'FREE_TRANSACTION_COUNT_REACHED') {
                let notificationService = this.$injector.get('notificationService');

                if (notificationService) {
                    this.$injector.get('sessionService').promise.then((session) => {
                        notificationService.show({
                            component: 'dbAEventLimitNotification',
                            id: 'event-limit',
                            bindings: {
                                plan: session.plan
                            }
                        }, true, 0);
                    });

                    return this.$q;
                }
            }

            result.status = Math.max(result.status || 0, 0);

            if (result.status === 401) {
                this.diagnosticsService.warn('api_request_401', {
                    hasToken: !!this.token,
                    error: result.data && result.data.error,
                    message: result.data && result.data.message
                });
            } else if (result.status >= 500) {
                this.diagnosticsService.error('api_request_server_error', {
                    status: result.status,
                    error: result.data && result.data.error
                });
            }

            this.fire('error', result);
            this.fire(result.status);

            return this.$q.reject(result);
        });
    }

    requestTokenRefresh() {
        if (!this.tokenExpiredPromise) {
            this.tokenExpiredPromise = this.$q.defer();
            this.diagnosticsService.info('api_token_refresh_requested', {
                hasToken: !!this.token
            });

            this.tokenExpiredPromise.promise.catch(() => null).finally(() => this.tokenExpiredPromise = null);

            this.fire('token-expired', this.tokenExpiredPromise);
        }

        return this.tokenExpiredPromise.promise;
    }
    
    onError(callback) {
        return this.on('error', callback);
    }

    onStatusCode(code, callback) {
        return this.on(code, callback);
    }

    onTokenExpired(callback) {
        return this.on('token-expired', callback);
    }

    updateUrl(url) {
        return this.api ? this.api + url : url;
    }
}



// WEBPACK FOOTER //
// ./src/common/service/communication/api.service.js